import type { LegacyProductCode } from '@up2stay/up2promo-api-client';

import { env } from '@/core/env';
import { parseSetupTypeParam } from '@/core/lib/product-setup-type';
import { shellPaths } from '@/core/router/app-paths';

const PRODUCTION_PROFILE_VIEWER_HOSTS = new Set(['profile.up2promo.com']);

const PRODUCTION_PRODUCT_LINK_HOSTS = new Set([
  // Internal forwarder hosts (ADR-0026 §8).
  'forwarder.prod.up2promo.com',
  'forwarder.dev.up2promo.com',
  'link.up2promo.com',
  'link.up2promo.de',
  'up2.page',
]);

const PRODUCTION_APP_HOSTS = new Set(['app.up2promo.com']);

const APP_PATH_PREFIXES = new Set([
  'tabs',
  'auth',
  'link-error',
  'claim',
  'public-profile',
]);

export type NativeLinkDestination =
  | {
      kind: 'profile';
      workspaceUsername: string;
      username: string;
    }
  | {
      /** Digital profile `/p/{publicHash}` — not a product. */
      kind: 'profile-hash';
      hash: string;
      /** `?src=` attribution (wallet | profile_qr | …). */
      src?: string;
    }
  | {
      kind: 'product';
      hash: string;
      /** Product kind from QR `?t=` — api-client enum. */
      productType?: LegacyProductCode;
      /** Short product scan channel from `?s=` (QR | NT). */
      scanSource?: string;
    }
  | {
      kind: 'app';
      path: string;
    }
  | {
      kind: 'external';
      url: string;
    };

function hostnameFromEnvUrl(value: string | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    const normalized = value.includes('://') ? value : `https://${value}`;
    return new URL(normalized).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isLocalDevHost(host: string): boolean {
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.local') ||
    host.startsWith('192.168.') ||
    host.startsWith('10.') ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host)
  );
}

function pathSegments(pathname: string): string[] {
  return pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    });
}

/** `/workspace/acme/jane` (legacy) or `/acme/jane`. */
export function parseProfilePathname(
  pathname: string,
): { workspaceUsername: string; username: string } | null {
  const parts = pathSegments(pathname);
  if (parts[0] === 'workspace' && parts[1] && parts[2]) {
    return { workspaceUsername: parts[1], username: parts[2] };
  }
  if (
    parts.length >= 2 &&
    parts[0] &&
    parts[1] &&
    !APP_PATH_PREFIXES.has(parts[0])
  ) {
    return { workspaceUsername: parts[0], username: parts[1] };
  }
  return null;
}

function profileViewerHosts(): Set<string> {
  const hosts = new Set(PRODUCTION_PROFILE_VIEWER_HOSTS);
  const fromEnv = hostnameFromEnvUrl(env.VITE_PROFILE_VIEWER_DOMAIN);
  if (fromEnv) hosts.add(fromEnv);
  return hosts;
}

function productLinkHosts(): Set<string> {
  const hosts = new Set(PRODUCTION_PRODUCT_LINK_HOSTS);
  const fromEnv = hostnameFromEnvUrl(env.VITE_PRODUCT_LINK_ORIGIN);
  if (fromEnv) hosts.add(fromEnv);
  return hosts;
}

export function classifyNativeLink(url: URL): NativeLinkDestination {
  const host = url.hostname.toLowerCase();
  const segments = pathSegments(url.pathname);
  const profile = parseProfilePathname(url.pathname);
  const devHost = isLocalDevHost(host);

  if (profile && (profileViewerHosts().has(host) || devHost)) {
    return { kind: 'profile', ...profile };
  }

  if (
    segments.length === 2 &&
    segments[0] === 'p' &&
    segments[1] &&
    (productLinkHosts().has(host) || devHost)
  ) {
    const src = url.searchParams.get('src')?.trim() || undefined;
    return src
      ? { kind: 'profile-hash', hash: segments[1], src }
      : { kind: 'profile-hash', hash: segments[1] };
  }

  const hash = segments[0];
  if (
    hash &&
    segments.length === 1 &&
    (productLinkHosts().has(host) || devHost)
  ) {
    const productType = url.searchParams.get('t')?.trim() || undefined;
    const parsedType = parseSetupTypeParam(productType);
    const scanSource = url.searchParams.get('s')?.trim() || undefined;
    if (parsedType && scanSource) {
      return {
        kind: 'product',
        hash,
        productType: parsedType,
        scanSource,
      };
    }
    if (parsedType) {
      return { kind: 'product', hash, productType: parsedType };
    }
    if (scanSource) {
      return { kind: 'product', hash, scanSource };
    }
    return { kind: 'product', hash };
  }

  if (
    PRODUCTION_APP_HOSTS.has(host) ||
    (devHost && APP_PATH_PREFIXES.has(segments[0] ?? ''))
  ) {
    return { kind: 'app', path: `${url.pathname}${url.search}${url.hash}` };
  }

  return { kind: 'external', url: url.toString() };
}

export function buildStandaloneProfilePath(
  workspaceUsername: string,
  username: string,
  scanHash?: string,
): string {
  const path = shellPaths.leadsPublicProfilePath(workspaceUsername, username);
  return scanHash ? `${path}?scanHash=${encodeURIComponent(scanHash)}` : path;
}

/**
 * Re-attaches QR `?t=` when a not-setup mapping resolves to `/{hash}`
 * (setup frontend). Configured destinations keep their own query string.
 */
export function withScannedProductType(
  targetUrl: string,
  hash: string,
  productType?: LegacyProductCode | string | null,
): string {
  const type = parseSetupTypeParam(
    productType == null ? undefined : String(productType),
  );
  if (!type || !hash.trim()) return targetUrl;
  try {
    const url = new URL(targetUrl);
    if (url.searchParams.has('t')) return targetUrl;
    const segments = pathSegments(url.pathname);
    if (segments.length !== 1 || segments[0] !== hash) return targetUrl;
    url.searchParams.set('t', type);
    return url.toString();
  } catch {
    return targetUrl;
  }
}

export function parseProfileViewerUrl(
  value: string,
): { workspaceUsername: string; username: string } | null {
  try {
    const destination = classifyNativeLink(new URL(value));
    if (destination.kind === 'profile') {
      return {
        workspaceUsername: destination.workspaceUsername,
        username: destination.username,
      };
    }
    return parseProfilePathname(new URL(value).pathname);
  } catch {
    return null;
  }
}
